import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Pencil, Trash2, Copy, ExternalLink, Eye, TrendingUp, Package } from 'lucide-react';

/**
 * Extracted from AdminPackages.jsx to reduce component size.
 * Renders the packages table with actions.
 */
export default function PackageTable({
  filteredPackages,
  openEditModal,
  setDeleteConfirm,
  togglePackageActive,
  copyPackageLink,
  openPackageInNewTab,
}) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="text-left text-sm text-slate-600">
                <th className="p-4 font-semibold">Code</th>
                <th className="p-4 font-semibold">Package</th>
                <th className="p-4 font-semibold">Destination</th>
                <th className="p-4 font-semibold">Price</th>
                <th className="p-4 font-semibold">Category</th>
                <th className="p-4 font-semibold">Types</th>
                <th className="p-4 font-semibold">Performance</th>
                <th className="p-4 font-semibold">Status</th>
                <th className="p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPackages.map((pkg) => {
                const pkgTypes = pkg.types || (pkg.type ? [pkg.type] : []);
                return (
                  <tr key={pkg.id} className={`border-b hover:bg-slate-50 ${!pkg.is_active ? 'bg-slate-50/50' : ''}`}>
                    <td className="p-4">
                      <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">#{pkg.package_code}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img src={pkg.image} alt={pkg.title} className={`w-12 h-12 rounded-lg object-cover ${!pkg.is_active ? 'opacity-50' : ''}`} />
                        <div>
                          <p className={`font-medium ${!pkg.is_active ? 'text-slate-500' : 'text-slate-800'}`}>{pkg.title}</p>
                          <p className="text-sm text-slate-500">{pkg.duration}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">{pkg.destination}</td>
                    <td className="p-4 font-semibold text-slate-800">₹{pkg.price?.toLocaleString()}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${pkg.category === 'international' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                        {pkg.category}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        {pkgTypes.slice(0, 2).map(type => (
                          <span key={type} className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 capitalize">{type}</span>
                        ))}
                        {pkgTypes.length > 2 && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">+{pkgTypes.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><Eye size={14} /> {pkg.views || 0}</span>
                        <span className="flex items-center gap-1"><TrendingUp size={14} /> {pkg.enquiries || 0}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => togglePackageActive(pkg)}
                        className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${pkg.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                        title={pkg.is_active ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {pkg.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => copyPackageLink(pkg)} className="text-gray-600 hover:text-gray-700 hover:bg-gray-50" title="Copy link"><Copy size={16} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => openPackageInNewTab(pkg)} className="text-gray-600 hover:text-gray-700 hover:bg-gray-50" title="Open in new tab"><ExternalLink size={16} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditModal(pkg)} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-testid={`edit-pkg-${pkg.package_code}`}><Pencil size={16} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteConfirm(pkg)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-pkg-${pkg.package_code}`}><Trash2 size={16} /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredPackages.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No packages found</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
